import { useEffect, useMemo, useState } from 'react'
import { useI18n } from '@/shared/i18n/useT'
import type { DeviceScope } from '@/shared/device/device'

export type ChartType = 'columns' | 'bars' | 'line' | 'pie'

export type ChartSort = 'desc' | 'asc' | 'original'

/** What the chart panel stores per sheet. Columns are kept by key, which survives reordering. */
export interface GridChartSettings {
  type?: ChartType
  category?: string | undefined
  series?: string[]
  showLabels?: boolean
  sort?: ChartSort
  /** The chart panel is open on this sheet. */
  open?: boolean
  /** Before `splits`: the one sheet the split view showed. Read, never written. */
  split?: string | null
  /** The devices the open chart panel shows on. Missing means every device. */
  openOn?: DeviceScope
  /** The sheets the split view shows beside this one, left to right; two at most. */
  splits?: string[]
  /** The devices the split view shows on. Missing means every device. */
  splitsOn?: DeviceScope
  /** Per split sheet id: the column fields its sidebar hides. */
  splitHidden?: Record<string, string[]>
  /** Date categories only: first and last day plotted (YYYY-MM-DD), inclusive. */
  dateFrom?: string | undefined
  dateTo?: string | undefined
  /** Date categories only: plot one point every `count` `unit`s instead of every row. */
  every?: ChartEvery | undefined
}

export type ChartEveryUnit = 'days' | 'weeks' | 'months' | 'years'
export interface ChartEvery { count: number; unit: ChartEveryUnit }

/** `iso` plus one step, as YYYY-MM-DD (UTC, so no daylight-saving drift). */
export function addChartStep(iso: string, every: ChartEvery): string {
  const d = new Date(`${iso}T00:00:00Z`)
  const n = Math.max(1, Math.floor(every.count))
  if (every.unit === 'days') d.setUTCDate(d.getUTCDate() + n)
  else if (every.unit === 'weeks') d.setUTCDate(d.getUTCDate() + 7 * n)
  else if (every.unit === 'months') d.setUTCMonth(d.getUTCMonth() + n)
  else d.setUTCFullYear(d.getUTCFullYear() + n)
  return d.toISOString().slice(0, 10)
}

/**
 * Keeps the points inside [from, to] and, with `every`, the first point of
 * each step counted from the earliest one kept. Points without a date are
 * dropped only when a filter is active. Order is preserved.
 */
export function filterChartPoints(points: ChartPoint[], from?: string, to?: string, every?: ChartEvery): ChartPoint[] {
  if (!from && !to && !every) return points
  const inRange = points.filter((p) => p.date !== undefined && (!from || p.date >= from) && (!to || p.date <= to))
  if (!every) return inRange
  const keep = new Set<ChartPoint>()
  let next: string | null = null
  for (const point of [...inRange].sort((a, b) => a.date!.localeCompare(b.date!))) {
    if (next === null || point.date! >= next) { keep.add(point); next = addChartStep(point.date!, every) }
  }
  return inRange.filter((p) => keep.has(p))
}

export interface ChartColumnOption {
  index: number
  /** Stable column key, used to store the choice. */
  key: string
  label: string
  /** Category columns only: holds dates, the only categories a line chart accepts. */
  isDate?: boolean
}

export interface ChartPoint {
  category: string
  values: number[]
  /** Each value as its source column displays it; missing ones fall back to a compact number. */
  labels?: (string | undefined)[]
  /** The category as YYYY-MM-DD, when the category column holds dates. */
  date?: string
}

const labelOf = (point: ChartPoint, series: number) => point.labels?.[series] || formatNumber(point.values[series] ?? 0)

interface GridChartPanelProps {
  width: number
  /** Text or date columns usable as categories. */
  categoryColumns: ChartColumnOption[]
  /** Number columns usable as series. */
  numberColumns: ChartColumnOption[]
  /** Builds the points for the chosen columns from the rows the grid currently shows. */
  buildPoints: (categoryIndex: number, seriesIndexes: number[]) => ChartPoint[]
  /** The sheet's saved settings, if any. */
  settings?: GridChartSettings | undefined
  onSettingsChange: (settings: GridChartSettings) => void
  /** Viewers see the saved chart but cannot change it. */
  readOnly?: boolean
  /** Absent for someone who may not close the shared panel. */
  onClose?: (() => void) | undefined
  /** Shown beside the close button: the grid puts the panel's device switch here. */
  headerExtra?: React.ReactNode
}

const PALETTE = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#64748b']

const SORT_ICONS: Record<ChartSort, React.ReactNode> = {
  desc: <><path d="M3 5h18" /><path d="M3 10h13" /><path d="M3 15h8" /><path d="M3 20h4" /></>,
  asc: <><path d="M3 5h4" /><path d="M3 10h8" /><path d="M3 15h13" /><path d="M3 20h18" /></>,
  original: <><path d="M3 5h10" /><path d="M3 10h18" /><path d="M3 15h6" /><path d="M3 20h14" /></>,
}

const TYPE_ICONS: Record<ChartType, React.ReactNode> = {
  columns: <><path d="M3 21h18" /><rect x="5" y="11" width="3" height="8" /><rect x="10.5" y="5" width="3" height="14" /><rect x="16" y="8" width="3" height="11" /></>,
  bars: <><path d="M3 3v18" /><rect x="5" y="5" width="8" height="3" /><rect x="5" y="10.5" width="14" height="3" /><rect x="5" y="16" width="11" height="3" /></>,
  line: <><path d="M3 3v18h18" /><path d="m6 15 4-5 4 3 5-7" /></>,
  pie: <><path d="M21 12A9 9 0 1 1 12 3v9z" /><path d="M15 3.5A9 9 0 0 1 20.5 9H15z" /></>,
}

function formatNumber(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1e9) return `${+(value / 1e9).toFixed(1)}B`
  if (abs >= 1e6) return `${+(value / 1e6).toFixed(1)}M`
  if (abs >= 1e4) return `${+(value / 1e3).toFixed(1)}k`
  return String(+value.toFixed(2))
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, Math.max(1, max - 1))}…` : text
}

/** Round axis bounds and ticks containing every value (and zero). */
function niceScale(values: number[], tickCount = 5) {
  let min = Math.min(0, ...values)
  let max = Math.max(0, ...values)
  if (min === max) max = min + 1
  const rough = (max - min) / tickCount
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ?? rough
  min = Math.floor(min / step) * step
  max = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let v = min; v <= max + step / 2; v += step) ticks.push(+v.toPrecision(12))
  return { min, max, ticks }
}

function ChartSvg({ type, points, seriesLabels, width, showLabels }: { type: ChartType; points: ChartPoint[]; seriesLabels: string[]; width: number; showLabels: boolean }) {
  if (type === 'pie') {
    const slices = points.map((p, i) => ({ label: p.category, value: Math.max(0, p.values[0] ?? 0), text: labelOf(p, 0), color: PALETTE[i % PALETTE.length] }))
      .filter((s) => s.value > 0)
    const total = slices.reduce((sum, s) => sum + s.value, 0)
    const size = Math.min(width, 320)
    const r = size / 2 - 4
    const c = size / 2
    let angle = -Math.PI / 2
    return (
      <div className="space-y-2">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto block" role="img">
          {slices.length === 1 && slices[0]
            ? <circle cx={c} cy={c} r={r} fill={slices[0].color}><title>{`${slices[0].label}: ${slices[0].text}`}</title></circle>
            : slices.map((s, si) => {
              const sweep = (s.value / total) * Math.PI * 2
              const a0 = angle
              const a1 = angle + sweep
              angle = a1
              const d = `M${c},${c} L${c + r * Math.cos(a0)},${c + r * Math.sin(a0)} A${r},${r} 0 ${sweep > Math.PI ? 1 : 0} 1 ${c + r * Math.cos(a1)},${c + r * Math.sin(a1)} Z`
              return <path key={si} d={d} fill={s.color} stroke="var(--color-surface, #fff)" strokeWidth={1}>
                <title>{`${s.label}: ${s.text} (${((s.value / total) * 100).toFixed(1)}%)`}</title>
              </path>
            })}
          {showLabels && (() => {
            let a = -Math.PI / 2
            return slices.map((s, si) => {
              const sweep = (s.value / total) * Math.PI * 2
              const mid = a + sweep / 2
              a += sweep
              if (slices.length > 1 && sweep < 0.25) return null
              const lr = slices.length === 1 ? 0 : r * 0.62
              const x = c + lr * Math.cos(mid)
              const y = c + lr * Math.sin(mid)
              return <text key={si} x={x} y={y} textAnchor="middle" fontSize={10} fill="#fff" stroke="rgba(0,0,0,0.35)" strokeWidth={2} paintOrder="stroke" pointerEvents="none">
                <tspan x={x} dy="-0.2em">{truncate(s.label, 14)}</tspan>
                <tspan x={x} dy="1.2em" fontWeight={600}>{s.text}</tspan>
              </text>
            })
          })()}
        </svg>
        <ul className="space-y-0.5 text-xs">
          {slices.map((s, si) => (
            <li key={si} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
              <span className="min-w-0 flex-1 truncate">{s.label}</span>
              <span className="text-text-muted tabular-nums">{((s.value / total) * 100).toFixed(1)}%</span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const all = points.flatMap((p) => p.values)
  // Labels sit past the bar end; padding the range keeps the longest ones inside the plot.
  const pad = showLabels && type !== 'line' ? 0.12 : 0
  const { min, max, ticks } = niceScale([...all, Math.max(0, ...all) * (1 + pad), Math.min(0, ...all) * (1 + pad)])
  const horizontal = type === 'bars'
  const labelChars = horizontal ? 12 : 10
  const left = horizontal ? Math.min(90, 8 + labelChars * 6) : 40
  const bottom = horizontal ? 20 : 44
  const top = 8
  const right = 8
  const bandCount = Math.max(1, points.length)
  const height = horizontal ? Math.max(160, top + bottom + bandCount * Math.max(14, 8 * seriesLabels.length + 6)) : 240
  const plotW = Math.max(10, width - left - right)
  const plotH = Math.max(10, height - top - bottom)
  const valueLen = horizontal ? plotW : plotH
  const scale = (v: number) => ((v - min) / (max - min)) * valueLen
  const band = (horizontal ? plotH : plotW) / bandCount
  const zero = scale(0)
  const showEvery = horizontal ? 1 : Math.ceil(points.length / Math.max(1, Math.floor(plotW / 28)))

  return (
    <div className="space-y-2">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block text-text-muted" role="img">
        <g transform={`translate(${left},${top})`} fontSize={10} fill="currentColor">
          {ticks.map((tick) => {
            const pos = scale(tick)
            return horizontal
              ? <g key={tick}><line x1={pos} x2={pos} y1={0} y2={plotH} stroke="currentColor" strokeOpacity={0.15} /><text x={pos} y={plotH + 13} textAnchor="middle">{formatNumber(tick)}</text></g>
              : <g key={tick}><line x1={0} x2={plotW} y1={plotH - pos} y2={plotH - pos} stroke="currentColor" strokeOpacity={0.15} /><text x={-4} y={plotH - pos + 3} textAnchor="end">{formatNumber(tick)}</text></g>
          })}
          {points.map((p, i) => {
            const center = band * i + band / 2
            if (i % showEvery !== 0) return null
            return horizontal
              ? <text key={i} x={-4} y={center + 3} textAnchor="end"><title>{p.category}</title>{truncate(p.category, labelChars)}</text>
              : <text key={i} transform={`translate(${center},${plotH + 8}) rotate(-35)`} textAnchor="end"><title>{p.category}</title>{truncate(p.category, labelChars)}</text>
          })}
          {type === 'line'
            ? seriesLabels.map((label, s) => {
              const color = PALETTE[s % PALETTE.length]
              const coords = points.map((p, i) => [band * i + band / 2, plotH - scale(p.values[s] ?? 0)] as const)
              return <g key={label + s}>
                <polyline points={coords.map(([x, y]) => `${x},${y}`).join(' ')} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
                {coords.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={points.length > 40 ? 1.5 : 3} fill={color}>
                  <title>{`${points[i]?.category} · ${label}: ${points[i] ? labelOf(points[i], s) : ''}`}</title>
                </circle>)}
              </g>
            })
            : points.map((p, i) => {
              const inner = band * 0.8
              const barSize = inner / Math.max(1, p.values.length)
              return p.values.map((v, s) => {
                const pos = scale(v)
                const offset = band * i + band * 0.1 + barSize * s
                const start = Math.min(zero, pos)
                const len = Math.abs(pos - zero)
                const color = PALETTE[s % PALETTE.length]
                const title = <title>{`${p.category}${seriesLabels.length > 1 ? ` · ${seriesLabels[s]}` : ''}: ${labelOf(p, s)}`}</title>
                const negative = v < 0
                const label = showLabels && barSize >= 9
                  ? horizontal
                    ? <text x={negative ? start - 3 : start + len + 3} y={offset + barSize / 2 + 3} textAnchor={negative ? 'end' : 'start'} className="fill-text">{labelOf(p, s)}</text>
                    : <text x={offset + barSize / 2} y={negative ? plotH - start + 10 : plotH - start - len - 3} textAnchor="middle" className="fill-text">{labelOf(p, s)}</text>
                  : null
                return <g key={`${i}-${s}`}>
                  {horizontal
                    ? <rect x={start} y={offset} width={len} height={Math.max(1, barSize - 1)} fill={color} rx={1}>{title}</rect>
                    : <rect x={offset} y={plotH - start - len} width={Math.max(1, barSize - 1)} height={len} fill={color} rx={1}>{title}</rect>}
                  {label}
                </g>
              })
            })}
          {horizontal
            ? <line x1={zero} x2={zero} y1={0} y2={plotH} stroke="currentColor" strokeOpacity={0.5} />
            : <line x1={0} x2={plotW} y1={plotH - zero} y2={plotH - zero} stroke="currentColor" strokeOpacity={0.5} />}
        </g>
      </svg>
      {seriesLabels.length > 1 && (
        <ul className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
          {seriesLabels.map((label, s) => (
            <li key={label + s} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: PALETTE[s % PALETTE.length] }} />
              {label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function GridChartPanel({ width, categoryColumns, numberColumns, buildPoints, settings, onSettingsChange, readOnly = false, onClose, headerExtra }: GridChartPanelProps) {
  const { t } = useI18n()
  const indexOfKey = (options: ChartColumnOption[], key: string | undefined) => options.find((c) => c.key === key)?.index ?? null
  const fromSettings = (saved: GridChartSettings | undefined) => ({
    type: saved?.type ?? 'columns',
    categoryIndex: indexOfKey(categoryColumns, saved?.category),
    seriesIndexes: (saved?.series ?? []).map((key) => indexOfKey(numberColumns, key)).filter((i): i is number => i !== null),
    showLabels: saved?.showLabels ?? true,
    sort: saved?.sort ?? 'desc',
    dateFrom: saved?.dateFrom ?? '',
    dateTo: saved?.dateTo ?? '',
    every: saved?.every ?? null,
  })
  const initial = fromSettings(settings)
  const [type, setType] = useState<ChartType>(initial.type)
  const [categoryIndex, setCategoryIndex] = useState<number | null>(initial.categoryIndex)
  const [seriesIndexes, setSeriesIndexes] = useState<number[]>(initial.seriesIndexes)
  const [showLabels, setShowLabels] = useState(initial.showLabels)
  const [sort, setSort] = useState<ChartSort>(initial.sort)
  const [dateFrom, setDateFrom] = useState(initial.dateFrom)
  const [dateTo, setDateTo] = useState(initial.dateTo)
  const [every, setEvery] = useState<ChartEvery | null>(initial.every)

  // Follow the saved settings when they change from elsewhere (another sheet reader, a reload).
  const settingsJson = JSON.stringify(settings ?? null)
  useEffect(() => {
    const next = fromSettings(settings)
    setType(next.type)
    if (next.categoryIndex !== null) setCategoryIndex(next.categoryIndex)
    if (next.seriesIndexes.length > 0) setSeriesIndexes(next.seriesIndexes)
    setShowLabels(next.showLabels)
    setSort(next.sort)
    setDateFrom(next.dateFrom)
    setDateTo(next.dateTo)
    setEvery(next.every)
  }, [settingsJson]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Applies a user change locally and reports the full resulting settings for saving. */
  const commit = (change: Partial<{ type: ChartType; categoryIndex: number | null; seriesIndexes: number[]; showLabels: boolean; sort: ChartSort; dateFrom: string; dateTo: string; every: ChartEvery | null }>) => {
    if (readOnly) return
    const next = { type, categoryIndex, seriesIndexes, showLabels, sort, dateFrom, dateTo, every, ...change }
    if (change.dateFrom !== undefined) setDateFrom(change.dateFrom)
    if (change.dateTo !== undefined) setDateTo(change.dateTo)
    if (change.every !== undefined) setEvery(change.every)
    if (change.type !== undefined) setType(change.type)
    if (change.categoryIndex !== undefined) setCategoryIndex(change.categoryIndex)
    if (change.seriesIndexes !== undefined) setSeriesIndexes(change.seriesIndexes)
    if (change.showLabels !== undefined) setShowLabels(change.showLabels)
    if (change.sort !== undefined) setSort(change.sort)
    const keyOf = (options: ChartColumnOption[], index: number | null) => options.find((c) => c.index === index)?.key
    onSettingsChange({
      type: next.type,
      category: keyOf(categoryColumns, next.categoryIndex),
      series: next.seriesIndexes.map((i) => keyOf(numberColumns, i)).filter((k): k is string => k !== undefined),
      showLabels: next.showLabels,
      sort: next.sort,
      // Explicit undefined so clearing a filter overrides the saved value.
      dateFrom: next.dateFrom || undefined,
      dateTo: next.dateTo || undefined,
      every: next.every ?? undefined,
    })
  }

  const dateColumns = useMemo(() => categoryColumns.filter((c) => c.isDate), [categoryColumns])
  const lineAvailable = dateColumns.length > 0
  const isLine = type === 'line'
  const shownCategoryColumns = isLine ? dateColumns : categoryColumns

  // Line chart needs date categories: leave it when they disappear.
  useEffect(() => {
    if (isLine && !lineAvailable) setType('columns')
  }, [isLine, lineAvailable])

  // Default to the first available columns and drop picks whose column disappeared.
  useEffect(() => {
    if (categoryIndex === null || !shownCategoryColumns.some((c) => c.index === categoryIndex)) {
      setCategoryIndex(shownCategoryColumns[0]?.index ?? null)
    }
  }, [shownCategoryColumns, categoryIndex])
  useEffect(() => {
    setSeriesIndexes((current) => {
      const kept = current.filter((i) => numberColumns.some((c) => c.index === i))
      if (kept.length === 0 && numberColumns[0]) return [numberColumns[0].index]
      return kept.length === current.length ? current : kept
    })
  }, [numberColumns])

  const multiSeries = type === 'line'
  const activeSeries = multiSeries ? seriesIndexes : seriesIndexes.slice(0, 1)

  const rawPoints = useMemo(
    () => categoryIndex === null || activeSeries.length === 0 ? [] : buildPoints(categoryIndex, activeSeries),
    [buildPoints, categoryIndex, activeSeries.join(',')], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const categoryIsDate = categoryColumns.find((c) => c.index === categoryIndex)?.isDate === true
  // Pie slices have no time axis, so the date filters apply to the other three.
  const dateFilters = categoryIsDate && type !== 'pie'
  const points = useMemo(() => {
    const filtered = dateFilters ? filterChartPoints(rawPoints, dateFrom || undefined, dateTo || undefined, every ?? undefined) : rawPoints
    if (isLine || sort === 'original') return filtered
    const sign = sort === 'desc' ? -1 : 1
    return [...filtered].sort((a, b) => sign * ((a.values[0] ?? 0) - (b.values[0] ?? 0)))
  }, [rawPoints, isLine, sort, dateFilters, dateFrom, dateTo, every])
  const seriesLabels = activeSeries.map((i) => numberColumns.find((c) => c.index === i)?.label ?? '')

  const toggleSeries = (index: number) => {
    if (!multiSeries) { commit({ seriesIndexes: [index] }); return }
    commit({
      seriesIndexes: seriesIndexes.includes(index)
        ? seriesIndexes.length > 1 ? seriesIndexes.filter((i) => i !== index) : seriesIndexes
        : [...seriesIndexes, index],
    })
  }

  const types: ChartType[] = ['columns', 'bars', 'line', 'pie']
  const typeLabels: Record<ChartType, string> = {
    columns: t('grid.chartColumns'),
    bars: t('grid.chartBars'),
    line: t('grid.chartLine'),
    pie: t('grid.chartPie'),
  }

  return (
    <>
      <div className="flex shrink-0 items-center justify-between gap-1 border-b border-grid-line p-2">
        <div role="radiogroup" aria-label={t('grid.chartType')} className="flex gap-0.5 rounded-full border border-border p-0.5">
          {types.map((option) => (
            <button key={option} type="button" role="radio" aria-checked={type === option}
              title={option === 'line' && !lineAvailable ? t('grid.chartLineNeedsDates') : typeLabels[option]} aria-label={typeLabels[option]}
              disabled={readOnly ? type !== option : option === 'line' && !lineAvailable}
              onClick={() => commit({ type: option })}
              className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${type === option ? 'bg-brand/10 text-brand' : 'text-text-muted enabled:hover:bg-surface-hover'}`}>
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{TYPE_ICONS[option]}</svg>
            </button>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {headerExtra}
          {onClose && <button type="button" onClick={onClose} aria-label={t('common.close')}
            className="flex h-7 w-7 items-center justify-center rounded-full text-text-muted hover:bg-surface-3 hover:text-text">
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>}
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-2">
        <label className="block space-y-1">
          <span className="text-xs font-medium text-text-muted">{t('grid.chartCategories')}</span>
          <select value={categoryIndex ?? ''} onChange={(e) => commit({ categoryIndex: e.target.value === '' ? null : Number(e.target.value) })}
            disabled={readOnly || shownCategoryColumns.length === 0}
            className="h-8 w-full rounded-md border border-border bg-bg px-2 text-sm sm:text-xs outline-none focus:border-focus">
            {shownCategoryColumns.length === 0 && <option value="">{t('grid.chartNoCategoryColumns')}</option>}
            {shownCategoryColumns.map((c) => <option key={c.index} value={c.index}>{c.label}</option>)}
          </select>
        </label>
        {dateFilters && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <label className="block space-y-1">
                <span className="text-xs font-medium text-text-muted">{t('grid.chartStart')}</span>
                <input type="date" value={dateFrom} max={dateTo || undefined} disabled={readOnly}
                  onChange={(e) => commit({ dateFrom: e.target.value })}
                  className="h-8 w-full rounded-md border border-border bg-bg px-2 text-sm sm:text-xs outline-none focus:border-focus" />
              </label>
              <label className="block space-y-1">
                <span className="text-xs font-medium text-text-muted">{t('grid.chartEnd')}</span>
                <input type="date" value={dateTo} min={dateFrom || undefined} disabled={readOnly}
                  onChange={(e) => commit({ dateTo: e.target.value })}
                  className="h-8 w-full rounded-md border border-border bg-bg px-2 text-sm sm:text-xs outline-none focus:border-focus" />
              </label>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium text-text-muted">{t('grid.chartFrequency')}</span>
              <div className="flex gap-2">
                <input type="number" min={1} step={1} inputMode="numeric" aria-label={t('grid.chartFrequencyCount')}
                  value={every?.count ?? 1} disabled={readOnly || !every}
                  onChange={(e) => { const count = Math.max(1, Math.floor(Number(e.target.value) || 1)); if (every) commit({ every: { ...every, count } }) }}
                  className="h-8 w-16 rounded-md border border-border bg-bg px-2 text-sm sm:text-xs outline-none focus:border-focus disabled:opacity-50" />
                <select value={every?.unit ?? ''} disabled={readOnly} aria-label={t('grid.chartFrequency')}
                  onChange={(e) => commit({ every: e.target.value ? { count: every?.count ?? 1, unit: e.target.value as ChartEveryUnit } : null })}
                  className="h-8 min-w-0 flex-1 rounded-md border border-border bg-bg px-2 text-sm sm:text-xs outline-none focus:border-focus">
                  <option value="">{t('grid.chartFrequencyAll')}</option>
                  {(['days', 'weeks', 'months', 'years'] as const).map((unit) => (
                    <option key={unit} value={unit}>{t(`grid.chartFrequencyUnits.${unit}`)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-1">
          <span className="text-xs font-medium text-text-muted">{multiSeries ? t('grid.chartSeriesMulti') : t('grid.chartSeries')}</span>
          {numberColumns.length === 0
            ? <p className="text-xs text-text-muted">{t('grid.chartNoNumberColumns')}</p>
            : multiSeries
              ? <div className="max-h-36 space-y-0.5 overflow-y-auto rounded-md border border-border p-1">
                {numberColumns.map((c) => {
                  const position = seriesIndexes.indexOf(c.index)
                  return (
                    <label key={c.index} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm sm:text-xs hover:bg-surface-hover">
                      <input type="checkbox" checked={position !== -1} disabled={readOnly} onChange={() => toggleSeries(c.index)} />
                      {position !== -1 && <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: PALETTE[position % PALETTE.length] }} />}
                      <span className="truncate">{c.label}</span>
                    </label>
                  )
                })}
              </div>
              : <select value={activeSeries[0] ?? ''} disabled={readOnly} onChange={(e) => toggleSeries(Number(e.target.value))}
                className="h-8 w-full rounded-md border border-border bg-bg px-2 text-sm sm:text-xs outline-none focus:border-focus">
                {numberColumns.map((c) => <option key={c.index} value={c.index}>{c.label}</option>)}
              </select>}
        </div>
        {!isLine && (
          <div className="flex items-center justify-between gap-2">
            <label className="flex cursor-pointer items-center gap-1.5 text-sm sm:text-xs">
              <input type="checkbox" checked={showLabels} disabled={readOnly} onChange={(e) => commit({ showLabels: e.target.checked })} />
              {t('grid.chartDataLabels')}
            </label>
            <div role="radiogroup" aria-label={t('grid.chartSort')} className="flex gap-0.5 rounded-full border border-border p-0.5">
              {(['desc', 'asc', 'original'] as ChartSort[]).map((option) => {
                const label = t(option === 'desc' ? 'grid.chartSortDesc' : option === 'asc' ? 'grid.chartSortAsc' : 'grid.chartSortOriginal')
                return (
                  <button key={option} type="button" role="radio" aria-checked={sort === option} title={label} aria-label={label}
                    disabled={readOnly && sort !== option}
                    onClick={() => commit({ sort: option })}
                    className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${sort === option ? 'bg-brand/10 text-brand' : 'text-text-muted enabled:hover:bg-surface-hover'}`}>
                    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">{SORT_ICONS[option]}</svg>
                  </button>
                )
              })}
            </div>
          </div>
        )}
        <div className="border-t border-grid-line pt-3">
          {points.length === 0
            ? <p className="text-xs text-text-muted">{t('grid.chartEmpty')}</p>
            : <ChartSvg type={type} points={points} seriesLabels={seriesLabels} width={Math.max(120, width - 18)} showLabels={showLabels && !isLine} />}
        </div>
      </div>
    </>
  )
}
